import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClientService } from '@/services/ClientService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface AdvertiserFormProps {
  initialData?: {
    id?: number;
    name: string;
    address: string | null;
    clientId?: number | null;
  };
  isLoading?: boolean;
  onSubmit: (data: {
    name: string;
    address?: string;
    clientId?: number | null;
  }) => Promise<void>;
  onCancel: () => void;
}

export function AdvertiserForm({
  initialData,
  isLoading = false,
  onSubmit,
  onCancel,
}: AdvertiserFormProps) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    address: initialData?.address || '',
    clientId: initialData?.clientId !== undefined ? initialData.clientId : null, // Explicit null check
  });

  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch active clients for dropdown
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients-list-simple'],
    queryFn: () => ClientService.getClients({ limit: 100 }), // Fetch first 100 clients
  });

  const clients = clientsData?.data?.clients || [];

  // Determine if client has changed (for warning)
  const initialClientId = initialData?.clientId || null;
  const currentClientId = formData.clientId;
  const isClientChanged = initialData?.id && initialClientId !== currentClientId;

  // Find client names for warning message
  const oldClientName = clients.find(c => c.id === initialClientId)?.name || "Unassigned / Legacy";
  const newClientName = clients.find(c => c.id === currentClientId)?.name || "Unassigned";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleClientChange = (value: string) => {
    const numValue = value === "unassigned" ? null : parseInt(value);
    setFormData(prev => ({ ...prev, clientId: numValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Advertiser name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        ...(formData.address?.trim() && { address: formData.address.trim() }),
        clientId: formData.clientId,
      };

      await onSubmit(submitData);
    } catch (err: any) {
      setError(err?.message || 'Failed to save advertiser');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Client Reassignment Warning */}
      {isClientChanged && (
        <Alert variant="warning" className="bg-amber-50 border-amber-200 text-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle>Warning: Changing Client Assignment</AlertTitle>
          <AlertDescription className="text-sm mt-1">
            You are moving this advertiser from <strong>{oldClientName}</strong> to <strong>{newClientName}</strong>.
            <ul className="list-disc ml-4 mt-1 opacity-90">
              <li>Existing bookings will <strong>not</strong> be retroactively changed.</li>
              <li>New bookings created from now on will belong to the new client.</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <Label htmlFor="clientId">Client (Commercial Counterparty)</Label>
        <div className="mt-1">
          {clientsLoading ? (
            <div className="flex items-center space-x-2 h-10 border rounded px-3 text-sm text-muted-foreground bg-gray-50">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading clients...</span>
            </div>
          ) : (
            <Select
              value={formData.clientId?.toString() || "unassigned"}
              onValueChange={handleClientChange}
              disabled={isSubmitting || isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a client..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned" className="text-muted-foreground font-medium">
                  Unassigned / Legacy
                </SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id.toString()}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            The client entity responsible for billing.
          </p>
        </div>
      </div>

      <div>
        <Label htmlFor="name">Advertiser Name *</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Coca-Cola Ireland"
          disabled={isSubmitting || isLoading}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="address">Address</Label>
        <Textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="e.g., 123 Main Street, Dublin, Ireland"
          disabled={isSubmitting || isLoading}
          rows={3}
          className="mt-1 resize-none"
        />
      </div>

      <div className="flex gap-2 pt-4">
        <Button
          type="submit"
          disabled={isSubmitting || isLoading}
          className="flex-1"
        >
          {isSubmitting ? 'Saving...' : initialData?.id ? 'Update Advertiser' : 'Create Advertiser'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting || isLoading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
